import { PerpetualID, Token, BlockTimestamp, FundingEvent } from '@/api/spec';
import type { MarketBoost } from '@/common/boostedMarkets';
import { PriceDirectionType } from '@/feature/order_book/types';

export type MarketData = {
  id?: number; // Internal ID of the market, unique across all protocol instances
  instanceId?: number; // ID of the protocol instance the market operates on
  perpId?: PerpetualID;
  symbol?: string;
  assetDecimals: number;
  quote: Token;
  quoteDecimals: number;
  name: string;
  markPrice: number;
  oraclePrice: number;
  priceDirection: PriceDirectionType;
  lastPrice?: number; // todo: resolve ambiguity with markPrice
  midPrice?: number; // Mid price between best bid/ask
  bestBidPrice?: number;
  bestAskPrice?: number;
  change24hValue: number;
  change24hPercentage: number;
  fundingRateValue: number;
  fundingRateCountdownMinutes: number;
  volume: string;
  volumeValue?: number; // Numeric volume in quote currency, for sorting
  volumeSize: string; // Daily volume in size units
  openInterest: string;
  openInterestValue?: number; // Numeric open interest in quote currency, for sorting
  openInterestSize: string;
  openInterestSizeValue?: number; // Numeric open interest in size units; openInterestValue = this × oraclePrice
  totalCollateralLocked?: string;
  maintenanceMarginFraction: number;
  initialMarginFraction: number;
  category: string[];
  isOpen?: boolean;
  isFavorite?: boolean;
  makerFeeMicros?: bigint; // Base-tier fee, equals makerFeesMicros[0]
  takerFeeMicros?: bigint;
  makerFeesMicros?: bigint[]; // Per-tier fee ladder, index = account fee tier (Account.ft)
  takerFeesMicros?: bigint[];
  contractVersion?: number[]; // Exchange contract [major, minor, patch]; empty = unknown
  minPostingAmount?: number;
  minSettleAmount?: number;
  recycleFee?: number;
  orderRetryBlocks?: number;
  orderTtlBlocks?: number;
  orderMaxImpactP?: number;
  fundingIndexPrice?: number;
  fundingSum?: number; // Cumulative funding sum, scaled by fundingSumDivider
  fundingSumDivider?: number; // FundingEvent.div — literal divisor, already 10^exp
  fundingIntervalBlocks?: number;
  stateTimestamp?: BlockTimestamp;
  configTimestamp?: BlockTimestamp;
  // Timestamp of the APPLIED funding event.
  fundingBlock?: number;
  upcomingFundingEvent?: FundingEvent;
  // Calculated timestamp of the NEXT FUTURE funding event. Used for UI countdown
  nextFundingBlock?: number;
  version?: number; // Market version from API
  boost?: MarketBoost; // Boost active on the market, parsed from `boost_params`; absent when there is none.
};

/**
 * Effective fee for an account's tier: ladder entry (clamped to the last tier), falling back
 * to the legacy base fee when the feature flag is off or the backend ships no ladder.
 */
export function resolveTierFeeMicros(
  feesMicros: bigint[] | undefined,
  baseFeeMicros: bigint | undefined,
  feeTier: number | undefined,
  feeTiersEnabled: boolean | undefined
): bigint | undefined {
  if (!feeTiersEnabled || !feesMicros?.length) return baseFeeMicros;
  const index = Math.min(Math.max(feeTier ?? 0, 0), feesMicros.length - 1);
  return feesMicros[index];
}

const CLOSE_FEE_CONTRACT_VERSION = [1, 7, 5]; // v1.1.7.5 — first contract charging a fee on close/decrease

/** Contracts ≥ v1.1.7.5 charge the trading fee on close too; an unknown version keeps the pre-close-fee UI. */
export function marketChargesCloseFee(market: Pick<MarketData, 'contractVersion'> | undefined): boolean {
  const version = market?.contractVersion;
  if (!version || version.length !== CLOSE_FEE_CONTRACT_VERSION.length) return false;
  for (let i = 0; i < version.length; i++) {
    if (version[i] !== CLOSE_FEE_CONTRACT_VERSION[i]) return version[i] > CLOSE_FEE_CONTRACT_VERSION[i];
  }
  return true;
}
